class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    // Skip initialization if custom price filter is present
    if (this.querySelector('.custom-price-filter')) {
      return;
    }
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.initializeSliderBindings();
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }

  initializeSliderBindings() {
    const sliderContainer = this.querySelector('.price-slider');
    if (!sliderContainer) return;
    const minRange = sliderContainer.querySelector('input[data-role="min-range"]');
    const maxRange = sliderContainer.querySelector('input[data-role="max-range"]');
    const inputs = this.querySelectorAll('.field input');
    const minInput = inputs[0];
    const maxInput = inputs[1];

    // Ensure we have both range inputs
    if (!minRange || !maxRange) {
      console.warn('Price range slider: Missing min or max range input');
      return;
    }

    const toPercent = (value) => {
      const min = Number(minRange.getAttribute('min')) || 0;
      const max = Number(maxRange.getAttribute('max')) || 100;
      const clamped = Math.min(Math.max(Number(value), min), max);
      return ((clamped - min) / (max - min)) * 100;
    };

    const updateTrack = () => {
      const minPct = toPercent(minRange.value || minInput.value || 0);
      const maxPct = toPercent(maxRange.value || maxInput.value || maxRange.getAttribute('max'));
      sliderContainer.style.setProperty('--min', minPct + '%');
      sliderContainer.style.setProperty('--max', maxPct + '%');
      
      // Update price labels
      updatePriceLabels();
    };

    const updatePriceLabels = () => {
      const minLabel = sliderContainer.querySelector('.price-slider__label--min .price-slider__value');
      const maxLabel = sliderContainer.querySelector('.price-slider__label--max .price-slider__value');
      
      if (minLabel) {
        // Get the current value from the range input
        const minValue = parseFloat(minRange.value) || 0;
        const displayValue = Math.round(minValue);
        minLabel.textContent = displayValue;
        console.log('Updated min label:', displayValue, 'from range value:', minRange.value);
      }
      if (maxLabel) {
        // Get the current value from the range input
        const maxValue = parseFloat(maxRange.value) || parseFloat(maxRange.getAttribute('max')) || 100;
        const displayValue = Math.round(maxValue);
        maxLabel.textContent = displayValue;
        console.log('Updated max label:', displayValue, 'from range value:', maxRange.value);
      }
    };

    const syncCommon = () => {
      const minValue = minRange.value || 0;
      const maxValue = maxRange.value || maxRange.getAttribute('max');
      
      minInput.value = minValue;
      maxInput.value = maxValue;
      
      // Trigger change events for filtering
      minInput.dispatchEvent(new Event('change', { bubbles: true }));
      maxInput.dispatchEvent(new Event('change', { bubbles: true }));
      
      // Also trigger input events for real-time filtering
      minInput.dispatchEvent(new Event('input', { bubbles: true }));
      maxInput.dispatchEvent(new Event('input', { bubbles: true }));
      
      updateTrack();
    };

    const minDistance = Math.max(1, Number(minRange.getAttribute('step')) || 1);

    const onMinRangeInput = (event) => {
      event.stopPropagation();
      // Only process if this is the active slider or no dragging is happening
      if (isDragging && activeSlider !== 'min') return;
      
      // Ensure we're actually interacting with the min slider
      if (event.target !== minRange && isDragging) return;
      
      const minVal = Number(minRange.value);
      const maxVal = Number(maxRange.value);
      if (minVal > maxVal - minDistance) {
        minRange.value = maxVal - minDistance; // stop right before max thumb
      }
      
      // Update labels immediately for real-time feedback
      updatePriceLabels();
      // Skip input events to prevent triggering max slider
      syncCommon(true);
      // Manually trigger only min input events
      minInput.dispatchEvent(new Event('change', { bubbles: true }));
      minInput.dispatchEvent(new Event('input', { bubbles: true }));
    };

    const onMaxRangeInput = (event) => {
      event.stopPropagation();
      // Only process if this is the active slider or no dragging is happening
      if (isDragging && activeSlider !== 'max') return;
      
      // Ensure we're actually interacting with the max slider
      if (event.target !== maxRange && isDragging) return;
      
      const minVal = Number(minRange.value);
      const maxVal = Number(maxRange.value);
      if (maxVal < minVal + minDistance) {
        maxRange.value = minVal + minDistance; // stop right after min thumb
      }
      
      // Update labels immediately for real-time feedback
      updatePriceLabels();
      // Skip input events to prevent triggering min slider
      syncCommon(true);
      // Manually trigger only max input events
      maxInput.dispatchEvent(new Event('change', { bubbles: true }));
      maxInput.dispatchEvent(new Event('input', { bubbles: true }));
    };

    const syncFromInput = () => {
      const minValue = minInput.value || 0;
      const maxValue = maxInput.value || maxRange.getAttribute('max');
      
      minRange.value = minValue;
      maxRange.value = maxValue;
      
      // Update labels with initial values
      updatePriceLabels();
      updateTrack();
    };

    minRange.addEventListener('input', onMinRangeInput);
    maxRange.addEventListener('input', onMaxRangeInput);
    
    // Add real-time updates during dragging
    minRange.addEventListener('change', () => {
      updatePriceLabels();
    });
    maxRange.addEventListener('change', () => {
      updatePriceLabels();
    });
    
    // Add additional event listeners for better responsiveness
    minRange.addEventListener('mousemove', () => {
      if (isDragging && activeSlider === 'min') {
        updatePriceLabels();
      }
    });
    maxRange.addEventListener('mousemove', () => {
      if (isDragging && activeSlider === 'max') {
        updatePriceLabels();
      }
    });

    const setActive = (el) => {
      minRange.classList.remove('active');
      maxRange.classList.remove('active');
      el.classList.add('active');
    };

    // Add a flag to prevent interference
    let isDragging = false;
    let activeSlider = null;

    // Add mouse events for better interaction with proper event handling
    minRange.addEventListener('mousedown', (event) => {
      event.stopPropagation();
      isDragging = true;
      activeSlider = 'min';
      setActive(minRange);
    });
    maxRange.addEventListener('mousedown', (event) => {
      event.stopPropagation();
      isDragging = true;
      activeSlider = 'max';
      setActive(maxRange);
    });
    
    minRange.addEventListener('pointerdown', (event) => {
      event.stopPropagation();
      isDragging = true;
      activeSlider = 'min';
      setActive(minRange);
    });
    maxRange.addEventListener('pointerdown', (event) => {
      event.stopPropagation();
      isDragging = true;
      activeSlider = 'max';
      setActive(maxRange);
    });
    
    minRange.addEventListener('touchstart', (event) => {
      event.stopPropagation();
      isDragging = true;
      activeSlider = 'min';
      setActive(minRange);
    }, { passive: true });
    maxRange.addEventListener('touchstart', (event) => {
      event.stopPropagation();
      isDragging = true;
      activeSlider = 'max';
      setActive(maxRange);
    }, { passive: true });
    
    // Add focus events to ensure proper handling
    minRange.addEventListener('focus', (event) => {
      event.stopPropagation();
      setActive(minRange);
    });
    maxRange.addEventListener('focus', (event) => {
      event.stopPropagation();
      setActive(maxRange);
    });
    
    // Add click events as fallback
    minRange.addEventListener('click', (event) => {
      event.stopPropagation();
      setActive(minRange);
    });
    maxRange.addEventListener('click', (event) => {
      event.stopPropagation();
      setActive(maxRange);
    });

    // Add mouse up events to reset dragging state
    document.addEventListener('mouseup', () => {
      isDragging = false;
      activeSlider = null;
    });
    
    document.addEventListener('touchend', () => {
      isDragging = false;
      activeSlider = null;
    });
    
    minInput.addEventListener('change', syncFromInput);
    maxInput.addEventListener('change', syncFromInput);
    
    // Initialize with proper values
    syncFromInput();
    
    // Ensure labels are updated after a short delay to handle any async loading
    setTimeout(() => {
      updatePriceLabels();
    }, 100);
  }
}

customElements.define('price-range', PriceRange);

// Custom Price Filter Handler
class CustomPriceFilter {
  constructor(filterElement) {
    this.filterElement = filterElement;
    // Find the custom price filter container within the price-range element
    const customFilter = filterElement.querySelector('.custom-price-filter');
    if (!customFilter) {
      return;
    }
    
    this.container = customFilter;
    this.minSlider = customFilter.querySelector('.custom-price-filter__slider--min');
    this.maxSlider = customFilter.querySelector('.custom-price-filter__slider--max');
    this.minInput = filterElement.querySelector('.custom-price-filter__input-min');
    this.maxInput = filterElement.querySelector('.custom-price-filter__input-max');
    this.minHandleLabel = customFilter.querySelector('.custom-price-filter__handle-label--min');
    this.maxHandleLabel = customFilter.querySelector('.custom-price-filter__handle-label--max');
    this.minHandleValue = customFilter.querySelector('.custom-price-filter__handle-min-value');
    this.maxHandleValue = customFilter.querySelector('.custom-price-filter__handle-max-value');
    this.fillElement = customFilter.querySelector('.custom-price-filter__slider-fill');
    
    if (!this.minSlider || !this.maxSlider || !this.minInput || !this.maxInput) {
      console.warn('Custom price filter: Missing required elements');
      return;
    }
    
    // Mark as initialized to prevent duplicate initialization
    if (customFilter.dataset.initialized) {
      return;
    }
    customFilter.dataset.initialized = 'true';
    
    this.maxValue = parseFloat(this.maxSlider.getAttribute('max')) || 100;
    this.minDistance = 1;
    
    // Cache dimensions to avoid expensive getBoundingClientRect calls
    this.cachedContainerWidth = 0;
    this.cachedMinLabelWidth = 60; // fallback estimate
    this.cachedMaxLabelWidth = 60; // fallback estimate
    this.isDragging = false;
    
    this.init();
  }
  
  init() {
    // Initialize values and positions immediately with fast method
    this.updateDisplay();
    this.updateFillFast();
    
    // Cache dimensions after initial render, then do full positioning check
    setTimeout(() => {
      const sliderTrack = this.container.querySelector('.custom-price-filter__slider-track');
      const containerRect = sliderTrack ? sliderTrack.getBoundingClientRect() : null;
      this.cachedContainerWidth = containerRect ? containerRect.width : 0;
      
      if (this.minHandleLabel) {
        const minRect = this.minHandleLabel.getBoundingClientRect();
        this.cachedMinLabelWidth = minRect.width || 60;
      }
      if (this.maxHandleLabel) {
        const maxRect = this.maxHandleLabel.getBoundingClientRect();
        this.cachedMaxLabelWidth = maxRect.width || 60;
      }
      
      // Do full positioning check after dimensions are cached
      this.updateFill();
      
      // Ensure fill is initialized
      this.updateFillFast();
    }, 50);
    
    // Bind events
    this.minSlider.addEventListener('input', (e) => this.handleMinInput(e));
    this.maxSlider.addEventListener('input', (e) => this.handleMaxInput(e));
    this.minSlider.addEventListener('change', () => this.handleMinChange());
    this.maxSlider.addEventListener('change', () => this.handleMaxChange());
    
    // Sync from hidden inputs if they change
    if (this.minInput) {
      this.minInput.addEventListener('change', () => this.syncFromInputs());
    }
    if (this.maxInput) {
      this.maxInput.addEventListener('change', () => this.syncFromInputs());
    }
  }
  
  handleMinInput(e) {
    this.isDragging = true;
    const minVal = parseFloat(this.minSlider.value);
    const maxVal = parseFloat(this.maxSlider.value);
    
    // Prevent min from exceeding max
    if (minVal >= maxVal) {
      this.minSlider.value = Math.max(0, maxVal - this.minDistance);
    }
    
    // Fast update during dragging - skip expensive calculations
    this.updateDisplay();
    this.updateFillFast();
    this.syncToInputs();
  }
  
  handleMaxInput(e) {
    this.isDragging = true;
    const minVal = parseFloat(this.minSlider.value);
    const maxVal = parseFloat(this.maxSlider.value);
    
    // Prevent max from going below min
    if (maxVal <= minVal) {
      this.maxSlider.value = Math.min(this.maxValue, minVal + this.minDistance);
    }
    
    // Fast update during dragging - skip expensive calculations
    this.updateDisplay();
    this.updateFillFast();
    this.syncToInputs();
  }
  
  handleMinChange() {
    this.isDragging = false;
    this.syncToInputs();
    // Trigger filter update and do full positioning check
    this.updateFill();
    if (this.minInput) {
      this.minInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  
  handleMaxChange() {
    this.isDragging = false;
    this.syncToInputs();
    // Trigger filter update and do full positioning check
    this.updateFill();
    if (this.maxInput) {
      this.maxInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  
  // Fast update method for during dragging - labels stay fixed
  updateFillFast() {
    const minVal = parseFloat(this.minSlider.value) || 0;
    const maxVal = parseFloat(this.maxSlider.value) || this.maxValue;
    const minPercent = (minVal / this.maxValue) * 100;
    const maxPercent = (maxVal / this.maxValue) * 100;
    
    // Update fill element
    if (this.fillElement) {
      this.fillElement.style.setProperty('--min-percent', minPercent + '%');
      this.fillElement.style.setProperty('--max-percent', maxPercent + '%');
    }
    
    // Labels stay fixed at left and right - no positioning needed
    // Only values update, positions are handled by CSS
  }
  
  syncToInputs() {
    if (this.minInput) {
      this.minInput.value = Math.round(parseFloat(this.minSlider.value));
    }
    if (this.maxInput) {
      this.maxInput.value = Math.round(parseFloat(this.maxSlider.value));
    }
  }
  
  syncFromInputs() {
    if (this.minInput && this.minInput.value) {
      const minVal = parseFloat(this.minInput.value);
      if (!isNaN(minVal)) {
        this.minSlider.value = Math.max(0, Math.min(minVal, this.maxValue));
      }
    }
    if (this.maxInput && this.maxInput.value) {
      const maxVal = parseFloat(this.maxInput.value);
      if (!isNaN(maxVal)) {
        this.maxSlider.value = Math.min(this.maxValue, Math.max(maxVal, 0));
      }
    }
    this.updateDisplay();
    this.updateFill();
  }
  
  updateDisplay() {
    const minVal = Math.round(parseFloat(this.minSlider.value) || 0);
    const maxVal = Math.round(parseFloat(this.maxSlider.value) || this.maxValue);
    
    // Update handle labels only
    if (this.minHandleValue) {
      this.minHandleValue.textContent = minVal;
    }
    if (this.maxHandleValue) {
      this.maxHandleValue.textContent = maxVal;
    }
  }
  
  updateFill() {
    const minVal = parseFloat(this.minSlider.value) || 0;
    const maxVal = parseFloat(this.maxSlider.value) || this.maxValue;
    const minPercent = (minVal / this.maxValue) * 100;
    const maxPercent = (maxVal / this.maxValue) * 100;
    
    // Update fill element
    if (this.fillElement) {
      this.fillElement.style.setProperty('--min-percent', minPercent + '%');
      this.fillElement.style.setProperty('--max-percent', maxPercent + '%');
    }
    
    // Cache container width if not already cached or if dragging stopped
    if (this.cachedContainerWidth === 0 || !this.isDragging) {
      const sliderTrack = this.container.querySelector('.custom-price-filter__slider-track');
      const containerRect = sliderTrack ? sliderTrack.getBoundingClientRect() : null;
      this.cachedContainerWidth = containerRect ? containerRect.width : 0;
      
      // Cache label widths
      if (this.minHandleLabel) {
        const minRect = this.minHandleLabel.getBoundingClientRect();
        this.cachedMinLabelWidth = minRect.width || 60;
      }
      if (this.maxHandleLabel) {
        const maxRect = this.maxHandleLabel.getBoundingClientRect();
        this.cachedMaxLabelWidth = maxRect.width || 60;
      }
    }
    
    // Labels stay fixed at left and right - no positioning needed
    // Only update the fill element and values
    if (this.minHandleLabel) {
      this.minHandleLabel.style.setProperty('--min-percent', minPercent + '%');
      this.minHandleLabel.style.top = '-3rem';
    }
    
    if (this.maxHandleLabel) {
      this.maxHandleLabel.style.setProperty('--max-percent', maxPercent + '%');
      this.maxHandleLabel.style.top = '-3rem';
    }
  }
}

// Initialize custom price filters
function initializeCustomPriceFilters() {
  const priceRangeElements = document.querySelectorAll('price-range');
  priceRangeElements.forEach(priceRange => {
    const customFilter = priceRange.querySelector('.custom-price-filter');
    if (customFilter && !customFilter.dataset.initialized) {
      new CustomPriceFilter(priceRange);
    }
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeCustomPriceFilters);
} else {
  initializeCustomPriceFilters();
}

// Also initialize when new content is loaded (for dynamic updates)
if (typeof FacetFiltersForm !== 'undefined') {
  const originalRenderPage = FacetFiltersForm.prototype.renderPage;
  FacetFiltersForm.prototype.renderPage = function(...args) {
    const result = originalRenderPage.apply(this, args);
    setTimeout(() => {
      initializeCustomPriceFilters();
    }, 100);
    return result;
  };
}

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

// Filter Toggle Button Functionality
class FilterToggleButton {
  constructor() {
    this.init();
  }

  init() {
    const filterButton = document.querySelector('.filter-toggle-button');
    if (filterButton) {
      // Set initial state to open
      filterButton.setAttribute('aria-expanded', 'true');
      this.toggleFilterSections(true);
      
      filterButton.addEventListener('click', this.toggleFilters.bind(this));
    }
  }

  toggleFilters(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const isExpanded = button.getAttribute('aria-expanded') === 'true';
    
    // Toggle aria-expanded
    button.setAttribute('aria-expanded', !isExpanded);
    
    // Toggle the visibility of filter sections
    this.toggleFilterSections(!isExpanded);
  }

  toggleFilterSections(show) {
    // Get all filter sections (Best Filter, Kind of Product, Availability, Price)
    const filterSections = document.querySelectorAll('.best-filter-section, .kind-of-product-section, .facets__disclosure');
    
    filterSections.forEach(section => {
      if (show) {
        section.style.display = 'block';
        section.setAttribute('open', '');
      } else {
        section.style.display = 'none';
        section.removeAttribute('open');
      }
    });
  }
}

// Initialize the filter toggle button when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new FilterToggleButton();
  new SortByButton();
});

// Sort By Button Enhancement
class SortByButton {
  constructor() {
    this.init();
  }

  init() {
    // Find all sort by buttons
    this.initializeSortButtons();
    
    // Set up mutation observer to handle dynamically added content
    this.setupMutationObserver();
  }

  initializeSortButtons() {
    const sortByButtons = document.querySelectorAll('.facet-filters__field, .mobile-facets__sort');
    
    sortByButtons.forEach(button => {
      this.setupSortButton(button);
    });
  }

  setupSortButton(button) {
    const select = button.querySelector('select');
    if (select && !button.hasAttribute('data-sort-enhanced')) {
      // Mark as enhanced to avoid duplicate setup
      button.setAttribute('data-sort-enhanced', 'true');
      
      // Add click event to the entire button area
      button.addEventListener('click', (event) => {
        // Only trigger if not clicking on the select itself
        if (event.target !== select) {
          event.preventDefault();
          select.focus();
          select.click();
        }
      });
    }
  }

  setupMutationObserver() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check if the added node is a sort button or contains sort buttons
              if (node.classList && (node.classList.contains('facet-filters__field') || node.classList.contains('mobile-facets__sort'))) {
                this.setupSortButton(node);
              } else {
                // Check for sort buttons within the added node
                const sortButtons = node.querySelectorAll ? node.querySelectorAll('.facet-filters__field, .mobile-facets__sort') : [];
                sortButtons.forEach(button => this.setupSortButton(button));
              }
            }
          });
        }
      });
    });

    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
}
